import { Link, useParams } from 'react-router-dom';
import collectionsData from '../data/collections.json';
import { Icon, Ornament, Reveal } from '../components/Primitives';
import ProductCard from '../components/ProductCard';
import Seo from '../components/Seo';
import { CtaBand, PageHero } from '../components/Shared';
import NotFound from './NotFound';

const { collections, products } = collectionsData;

/* Editorial frame paired with each collection, so no page is products alone. */
const EDITORIAL = {
  'bridal-heritage': {
    image: '/assets/lookbook/sapphire-03.webp',
    alt: 'Bride in royal blue wearing a polki choker and long haar',
  },
  'kundan-polki': {
    image: '/assets/details/detail-04.webp',
    alt: 'Emerald and pearl polki choker worn at the collarbone',
  },
  necklaces: {
    image: '/assets/lookbook/ivory-08.webp',
    alt: 'Emerald and pearl polki choker with a long haar on ivory chikankari',
  },
  earrings: {
    image: '/assets/details/detail-02.webp',
    alt: 'Close view of an emerald and polki drop earring',
  },
  'bangles-bracelets': {
    image: '/assets/details/detail-01.webp',
    alt: 'Polki cocktail ring and bangle held in the hand',
  },
  rings: {
    image: '/assets/details/detail-05.webp',
    alt: 'Polki rings worn on the hand',
  },
};

export default function CollectionDetail() {
  const { slug } = useParams();
  const collection = collections.find((c) => c.slug === slug);

  // Unknown slug renders the 404 body rather than an empty shell.
  if (!collection) return <NotFound />;

  const items = products.filter((p) => p.in.includes(slug));
  const others = collections.filter((c) => c.slug !== slug).slice(0, 3);
  const art = EDITORIAL[slug];

  return (
    <>
      <Seo
        title={collection.name}
        description={`${collection.tagline} — ${collection.description}`}
        image={`/assets/lookbook/${collection.hero}.webp`}
      />

      <PageHero
        eyebrow={collection.eyebrow}
        title={collection.name}
        lede={collection.description}
        trail={[
          { label: 'Home', to: '/' },
          { label: 'Collections', to: '/collections' },
          { label: collection.name },
        ]}
      />

      {/* Products */}
      <section className="bg-cream-50 py-16 sm:py-24">
        <div className="shell">
          <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-royal-800/10 pb-6">
            <h2 className="display-md text-royal-900">{collection.tagline}</h2>
            <p className="text-[11px] uppercase tracking-wide2 text-royal-700/55">
              {items.length} {items.length === 1 ? 'piece' : 'pieces'}
            </p>
          </div>

          <div className="mt-12 grid gap-x-7 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 90}>
                <ProductCard product={p} priority={i < 3} />
              </Reveal>
            ))}
          </div>

          <p className="mx-auto mt-16 max-w-2xl border-t border-royal-800/10 pt-7 text-center text-[12.5px] leading-relaxed text-royal-700/55">
            Designs shown represent our current collection. Gold purity, stone
            detail and weight are confirmed piece by piece in the showroom, and
            every retail sale is billed with BIS hallmarking.
          </p>
        </div>
      </section>

      {/* Editorial break */}
      {art && (
        <section className="relative overflow-hidden bg-royal-800">
          <div className="grid lg:grid-cols-2">
            <div className="relative min-h-[24rem] lg:min-h-[36rem]">
              <img
                src={art.image}
                alt={art.alt}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="noise-navy flex items-center px-5 py-16 sm:px-10 lg:px-16">
              <Reveal className="max-w-lg">
                <p className="eyebrow-light">Worn</p>
                <h2 className="display-md mt-4 text-cream-50">
                  Made to sit right on the person wearing it
                </h2>
                <Ornament tone="light" className="mt-6 !justify-start" />
                <p className="mt-7 text-[15px] leading-relaxed text-cream-200/75">
                  Lengths, drops and clasps are adjusted in-house. A collar that sits
                  a centimetre too low changes the whole line of an outfit, so we fit
                  before we finish.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/lookbook" className="btn-outline-light">
                    See it worn
                    <Icon.Arrow className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* Cross-links */}
      <section className="bg-cream-100 py-16 sm:py-20">
        <div className="shell">
          <p className="eyebrow text-center">Continue</p>
          <h2 className="display-md mt-3 text-center text-royal-900">Other collections</h2>
          <Ornament className="mt-6" />

          <div className="mt-11 grid gap-6 sm:grid-cols-3">
            {others.map((c, i) => (
              <Reveal key={c.slug} delay={i * 90}>
                <Link
                  to={`/collections/${c.slug}`}
                  className="group flex items-center gap-5 border border-royal-800/10 bg-cream-50 p-5
                    transition-all duration-500 ease-luxe hover:border-gold-500/40 hover:shadow-plate"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden bg-royal-800 ring-1 ring-inset ring-gold-500/20">
                    <img
                      src={`/assets/lookbook/${c.hero}-sm.webp`}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover object-[center_15%]"
                    />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-normal text-royal-900">{c.name}</h3>
                    <span className="mt-1 inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide2 text-maroon-700">
                      View
                      <Icon.Arrow className="h-3 w-3 transition-transform duration-500 ease-luxe group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
